import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from './tokenStore';

const DEFAULT_REMOTE_DONATION_SERVICE_BASE_URL =
  'https://coh-api.myefb.app/api/v1';
const DEFAULT_REMOTE_APP_BACKEND_BASE_URL =
  'https://coh-api.myefb.app/api/v1';
const LOCAL_DONATION_SERVICE_PROXY_PATH = '/api/v1';
const LOCAL_APP_BACKEND_PROXY_PATH = '/admin-api/v1';

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const isLocalHostname = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
};

const resolveBaseUrl = (configuredValue: string | undefined, localUrl: string, remoteUrl: string) =>
  trimTrailingSlash(
    configuredValue || (isLocalHostname() ? localUrl : remoteUrl)
  );

export const donationServiceBaseUrl = resolveBaseUrl(
  process.env.REACT_APP_DONATION_SERVICE_URL,
  LOCAL_DONATION_SERVICE_PROXY_PATH,
  DEFAULT_REMOTE_DONATION_SERVICE_BASE_URL
);
export const appBackendBaseUrl = resolveBaseUrl(
  process.env.REACT_APP_APP_BACKEND_URL,
  LOCAL_APP_BACKEND_PROXY_PATH,
  DEFAULT_REMOTE_APP_BACKEND_BASE_URL
);
export const authServiceBaseUrl = resolveBaseUrl(
  process.env.REACT_APP_AUTH_SERVICE_URL,
  LOCAL_APP_BACKEND_PROXY_PATH,
  DEFAULT_REMOTE_APP_BACKEND_BASE_URL
);

export const getDisplayApiBaseUrl = (baseUrl: string) => {
  if (typeof window !== 'undefined' && baseUrl.startsWith('/')) {
    return `${window.location.origin}${baseUrl}`;
  }

  return baseUrl;
};

export const apiClient = axios.create({
  baseURL: donationServiceBaseUrl,
});

export const appBackendApiClient = axios.create({
  baseURL: appBackendBaseUrl,
});

const attachRequestInterceptor = (client: typeof apiClient) => {
  client.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

attachRequestInterceptor(apiClient);
attachRequestInterceptor(appBackendApiClient);

const readTokenResponse = (payload: any) => ({
  accessToken: payload?.accessToken || payload?.access_token || null,
  refreshToken: payload?.refreshToken || payload?.refresh_token || null,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  clearTokens();
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
};

const attachResponseInterceptor = (client: typeof apiClient) => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = 'Bearer ' + token;
              }
              return client(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          handleLogout();
          return Promise.reject(error);
        }

        try {
          const { data } = await axios.post(
            `${authServiceBaseUrl}/auth/refresh`,
            { refreshToken }
          );

          const refreshedTokens = readTokenResponse(data);
          if (!refreshedTokens.accessToken) {
            throw new Error('Refresh response did not include an access token.');
          }

          setAccessToken(refreshedTokens.accessToken);
          if (refreshedTokens.refreshToken) {
            setRefreshToken(refreshedTokens.refreshToken);
          }

          if (originalRequest.headers) {
            originalRequest.headers[
              'Authorization'
            ] = `Bearer ${refreshedTokens.accessToken}`;
          }

          processQueue(null, refreshedTokens.accessToken);
          return client(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          processQueue(refreshError as Error, null);
          handleLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

attachResponseInterceptor(apiClient);
attachResponseInterceptor(appBackendApiClient);
